import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, ErrorAlert, Field, PageHeader, Spinner } from '../components/ui';
import { CONTENT_GOALS, CONTENT_TYPES, LANGUAGES, PLATFORMS, PLATFORM_META } from '@kontentowo/config';
import type { Platform } from '@kontentowo/config';
import type { ContentVariant } from '@kontentowo/shared';
import type { GenerateResponse } from '../lib/api';
import { VariantImageControl } from '../components/VariantImage';
import { VariantVideoControl } from '../components/VariantVideo';
import { SocialSeoSummary } from '../components/SocialSeoSummary';

export function ContentStudioPage() {
  const [params] = useSearchParams();
  const { data: brands, loading: brandsLoading } = useAsync(() => api.brands.list(), []);

  const [brandId, setBrandId] = useState(params.get('brand') ?? '');
  const [topic, setTopic] = useState(params.get('topic') ?? '');
  const [ideaId, setIdeaId] = useState<string | null>(params.get('idea'));
  const [goal, setGoal] = useState('ACQUISITION');
  const [language, setLanguage] = useState('pl');
  const [contentType, setContentType] = useState('post');
  const [platforms, setPlatforms] = useState<Platform[]>(['FACEBOOK', 'INSTAGRAM', 'TIKTOK', 'TELEGRAM']);
  const [brief, setBrief] = useState('');

  const [result, setResult] = useState<GenerateResponse | null>(null);
  const [activePlatform, setActivePlatform] = useState<Platform | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const selectedBrand = useMemo(() => brands?.find((b) => b.id === brandId) ?? null, [brands, brandId]);

  useEffect(() => {
    if (!brandId && brands && brands.length > 0) setBrandId(brands[0]!.id);
  }, [brands, brandId]);

  // Apply brand defaults (language + platforms) exactly once per selected brand —
  // never after the user toggles platforms.
  const appliedBrandRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedBrand) return;
    if (appliedBrandRef.current === brandId) return;
    appliedBrandRef.current = brandId;
    setLanguage(selectedBrand.language);
    if (selectedBrand.publishConfig.defaultPlatforms?.length) {
      setPlatforms(selectedBrand.publishConfig.defaultPlatforms);
    }
  }, [selectedBrand, brandId]);

  const togglePlatform = (p: Platform) =>
    setPlatforms((list) => (list.includes(p) ? list.filter((x) => x !== p) : [...list, p]));

  const generate = async () => {
    if (!brandId || !topic.trim() || platforms.length === 0) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await api.content.generate({
        brandId,
        topic,
        goal: goal as never,
        language: language as never,
        platforms,
        contentType: contentType as never,
        brief: brief || null,
        asDraft: false,
        ideaId: ideaId ?? null,
      });
      setResult(res);
      setActivePlatform(res.variants[0]?.platform ?? null);
      if (ideaId) setIdeaId(null);
    } catch (err) {
      setError(err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Content Studio"
        subtitle="Wybierz markę, temat i platformy. Kontentowo wygeneruje osobne warianty — nie kopiuje tego samego posta wszędzie."
      />

      {brandsLoading && <Spinner />}

      <div className="grid grid--2">
        <Card title="Brief">
          <Field label="Brand">
            <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              <option value="">— wybierz markę —</option>
              {brands?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Temat">
            <input className="input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="np. Minigra robbery" />
          </Field>
          <div className="form-row">
            <Field label="Cel">
              <select className="select" value={goal} onChange={(e) => setGoal(e.target.value)}>
                {CONTENT_GOALS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Język">
              <select className="select" value={language} onChange={(e) => setLanguage(e.target.value)}>
                {LANGUAGES.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Typ treści">
            <select className="select" value={contentType} onChange={(e) => setContentType(e.target.value)}>
              {CONTENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Dodatkowy kontekst (opcjonalnie)">
            <textarea className="textarea" value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Np. nowa minigra, premiera w piątek, kod promocyjny…" />
          </Field>
          <Field label="Platformy">
            <div className="chips">
              {PLATFORMS.filter((p) => p !== 'OTHER').map((p) => (
                <span key={p} className={`chip${platforms.includes(p) ? ' chip--active' : ''}`} onClick={() => togglePlatform(p)}>
                  {PLATFORM_META[p].label}
                </span>
              ))}
            </div>
          </Field>
          <div className="btn-row">
            <Button variant="primary" onClick={generate} disabled={generating || !brandId || !topic.trim() || platforms.length === 0}>
              {generating ? 'Generowanie…' : 'Generate'}
            </Button>
            {result && (
              <Link className="btn" to={`/content/${result.item.id}`}>
                Otwórz wynik
              </Link>
            )}
          </div>
          <ErrorAlert error={error} />
        </Card>

        <Card title={result ? 'Wygenerowane warianty' : 'Podgląd'}>
          {!result && <p className="muted">Warianty pojawią się tutaj po kliknięciu Generate. Mock AI tworzy osobny tekst dla każdej platformy.</p>}
          {result && (
            <div className="stack">
              {result.similarity.flagged && (
                <div className="alert alert--warning">
                  Content Diversity: podobieństwo {Math.round(result.similarity.highest * 100)}% do istniejącej treści (próg{' '}
                  {Math.round(result.similarity.threshold * 100)}%). Rozważ rewrite.
                </div>
              )}
              <div className="inline">
                <Badge tone="info">{result.item.status}</Badge>
                <span className="muted">provider: {result.item.providerId} · model: {result.item.modelId}</span>
              </div>
              {result.brandBrain?.chars > 0 && (
                <div className="alert alert--info">
                  Brand Brain: użyto {result.brandBrain.documents} dok. · {result.brandBrain.chars} znaków kontekstu.
                </div>
              )}
              <div className="chips" role="tablist" aria-label="Platform copy preview">
                {result.variants.map((variant) => <button type="button" role="tab" aria-selected={activePlatform === variant.platform} className={`chip${activePlatform === variant.platform ? ' chip--active' : ''}`} key={variant.id} onClick={() => setActivePlatform(variant.platform)}>{PLATFORM_META[variant.platform].label}</button>)}
              </div>
              {result.variants.filter((variant) => variant.platform === (activePlatform ?? result.variants[0]?.platform)).map((v) => (
                <div key={v.id}>
                  <VariantPreview variant={v} />
                  <SocialSeoSummary variant={v} />
                  <VariantImageControl brandId={result.item.brandId} contentId={result.item.id} variant={v} />
                  <VariantVideoControl brandId={result.item.brandId} contentId={result.item.id} variant={v} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

export function VariantPreview({ variant }: { variant: ContentVariant }) {
  const meta = PLATFORM_META[variant.platform];
  return (
    <div className="variant">
      <div className="variant__head">
        <span className="platform-dot" style={{ background: meta?.color ?? '#888' }} />
        <span className="variant__platform">{meta?.label ?? variant.platform}</span>
        <div style={{ flex: 1 }} />
        <Badge tone="neutral">{variant.kind}</Badge>
        <Badge tone="neutral">{variant.aspectRatio}</Badge>
      </div>
      <div className="variant__body">
        {variant.title && <h4>{variant.title}</h4>}
        <div className="variant__text">{variant.body}</div>
        {variant.hashtags.length > 0 && <div className="variant__meta">{variant.hashtags.map((h) => <Badge key={h} tone="info">{h}</Badge>)}</div>}
        {variant.cta && (
          <div className="variant__meta">
            <Badge tone="success">CTA: {variant.cta}</Badge>
          </div>
        )}
      </div>
    </div>
  );
}
